'use client'

import React, { createContext, useContext, useState, useEffect } from 'react'
import { useAuth } from '@/hooks/useAuth'
import { supabase } from '@/lib/supabase'

interface SubscriptionContextType {
  hasSubscription: boolean
  subscriptionTier: 'free' | 'pro' | 'enterprise'
  currentPeriodEnd: Date | null
  isLoading: boolean
  refreshSubscription: () => Promise<void>
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined)

export const useSubscription = () => {
  const context = useContext(SubscriptionContext)
  if (!context) {
    throw new Error('useSubscription must be used within a SubscriptionProvider')
  }
  return context
}

export const SubscriptionProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { session } = useAuth()
  const [hasSubscription, setHasSubscription] = useState(false)
  const [subscriptionTier, setSubscriptionTier] = useState<'free' | 'pro' | 'enterprise'>('free')
  const [currentPeriodEnd, setCurrentPeriodEnd] = useState<Date | null>(null)
  const [isLoading, setIsLoading] = useState(true)

  const refreshSubscription = async () => {
    if (!session?.user?.id) {
      setHasSubscription(false)
      setSubscriptionTier('free')
      setCurrentPeriodEnd(null)
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    try {
      // Query user_subscriptions table directly (matches mobile app approach)
      // See: supremepm/src/services/subscription-capabilities.service.ts
      const { data, error } = await supabase
        .from('user_subscriptions')
        .select('tier, status, current_period_end')
        .eq('user_id', session.user.id)
        .eq('status', 'active')
        .single() as { data: { tier: string; status: string; current_period_end: string | null } | null; error: any }

      if (error || !data) {
        // No active subscription found - default to free
        setHasSubscription(false)
        setSubscriptionTier('free')
        setCurrentPeriodEnd(null)
      } else {
        // Map tier to context format
        const tier = data.tier
        let mappedTier: 'free' | 'pro' | 'enterprise' = 'free'
        if (tier === 'enterprise') {
          mappedTier = 'enterprise'
        } else if (tier === 'professional' || tier === 'essential') {
          mappedTier = 'pro'
        }

        setHasSubscription(true)
        setSubscriptionTier(mappedTier)
        setCurrentPeriodEnd(data.current_period_end ? new Date(data.current_period_end) : null)
      }
    } catch (error) {
      console.error('Subscription fetch error:', error)
      setHasSubscription(false)
      setSubscriptionTier('free')
      setCurrentPeriodEnd(null)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    refreshSubscription()
  }, [session?.user?.id])

  const value: SubscriptionContextType = {
    hasSubscription,
    subscriptionTier,
    currentPeriodEnd,
    isLoading,
    refreshSubscription,
  }

  return (
    <SubscriptionContext.Provider value={value}>
      {children}
    </SubscriptionContext.Provider>
  )
}
